import { Fragment, useMemo, useRef, type ReactNode } from 'react';
import { renderMarkdownBlocks } from '../shared/renderMarkdownBlocks';
import { normalizeStrayInlineHtml } from '@/utils/chatUtils/chatDefaultUtils';
import { cn } from '@/lib/utils';
import { decodeHtmlEntities } from './chatText';
import {
  StreamCaret,
  appendStreamCaret,
  blockRevealKey,
  shouldFadeInWholeAnswer,
  withRevealClass,
} from './chatStreamPolish';

/**
 * Above this the markdown pass is skipped and the text renders verbatim. The
 * legacy panel used the same ceiling; a reply that long is a runaway, and
 * parsing it would lock the main thread.
 */
const MAX_MARKDOWN_RENDER_CHARS = 50_000;

/** Shared by every branch, so reduced motion switches all of ORA-598 off. */
const CONTAINER_CLASS =
  'chat-motion break-words text-fg-primary [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_.chat-ui_table-wrap]:overflow-x-auto [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-border-subtle [&_th]:bg-surface-muted [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_td]:border [&_td]:border-border-subtle [&_td]:px-2 [&_td]:py-1.5 [&>:first-child]:mt-0 [&>:last-child]:mb-0';

/**
 * Splits streaming text into the part that is finished and the part still
 * arriving (ORA-460).
 *
 * The line is the unit, because markdown blocks are line-based: every
 * COMPLETED line can be parsed, and only the line the model is part-way
 * through has to render verbatim. That is what stops a table answer showing
 * raw pipes and asterisks for the whole of its stream and snapping into shape
 * at `text.done` (ORA-457 reviewer A) — the table renders once its header and
 * delimiter rows have landed, and grows a row at a time.
 */
export const splitCompletedMarkdown = (
  text: string
): { completed: string; pending: string } => {
  const lastNewline = text.lastIndexOf('\n');
  if (lastNewline < 0) return { completed: '', pending: text };
  return {
    completed: text.slice(0, lastNewline),
    pending: text.slice(lastNewline + 1),
  };
};

/**
 * Splits the completed text into the blocks that are FINISHED and the block
 * still being written (ORA-460 review).
 *
 * Re-parsing the whole completed prefix on every completed line is quadratic:
 * a 20,000-character answer arrives as a few hundred lines and would cost a few
 * hundred parses of an ever-longer string. A blank line ends a markdown block
 * here, so everything before the LAST blank line can be parsed once and
 * memoised, and only the block being written is re-parsed as lines land.
 */
export const splitSettledBlocks = (
  completed: string
): { settled: string; current: string } => {
  const boundary = completed.lastIndexOf('\n\n');
  if (boundary < 0) return { settled: '', current: completed };
  return {
    settled: completed.slice(0, boundary + 2),
    current: completed.slice(boundary + 2),
  };
};

interface ChatMarkdownProps {
  text: string;
  /**
   * While the answer is still arriving, only the COMPLETED lines are parsed
   * and the line being written renders as-is (ORA-460). Parsing every delta
   * would re-parse the whole answer on each one — a second of blocked main
   * thread on a long reply — so the parse is memoised on the completed prefix
   * and runs only when a line finishes.
   */
  isStreaming?: boolean;
  /**
   * The turn behind this text is still running (ORA-598). Wider than
   * `isStreaming`, which `text.done` clears: an answer the backend buffered
   * whole can arrive entirely on `text.done`, and that is exactly the case the
   * whole-answer fade exists for. History has no turn, so it never fades.
   */
  isTurnRunning?: boolean;
  /**
   * Draw the `[[watch …]]` token as a Watch again chip (a course student's
   * companion, Courses V1). Off, the token is the text the model wrote.
   */
  watchTokens?: boolean;
}

/**
 * Assistant text, rendered through Ora's ported markdown renderer.
 *
 * No `data` and no citation-click handler are passed: the new protocol has no
 * inline `[A1]`/`[pN]` markers, and citations render from structured data
 * beside the text.
 */
const ChatMarkdown = ({
  text,
  isStreaming = false,
  isTurnRunning = false,
  watchTokens = false,
}: ChatMarkdownProps) => {
  // The same normalisation the legacy panel applies: models sometimes emit
  // stray inline HTML (`<i>`, `<br>`) mid-markdown, and it has to become
  // markdown before the parser sees it or it renders as literal tags.
  const normalized = useMemo(() => normalizeStrayInlineHtml(text), [text]);
  // A runaway reply keeps the old behaviour on both paths: no parse at all.
  const tooLong = normalized.length > MAX_MARKDOWN_RENDER_CHARS;
  const streaming = isStreaming && !tooLong;
  /**
   * The whole-answer cross-fade (ORA-598), decided during render because the
   * class has to be on the container the first time that text paints — an
   * effect would apply it a frame late, and state would cost an extra render
   * per delta, which the thread's performance test forbids. Both refs are
   * write-once-then-sticky, so a re-render never re-triggers the animation.
   */
  const previousLength = useRef(0);
  const fadesInWholeAnswer = useRef(false);

  if (
    (isStreaming || isTurnRunning) &&
    !fadesInWholeAnswer.current &&
    shouldFadeInWholeAnswer(previousLength.current, normalized.length)
  ) {
    fadesInWholeAnswer.current = true;
  }

  previousLength.current = normalized.length;

  const containerClass = cn(
    // A stable hook for surfaces that theme the answer text from outside
    // (the light-mode desktop draws it straight on the water).
    'chat-markdown',
    CONTAINER_CLASS,
    fadesInWholeAnswer.current && 'animate-chat-fade-in'
  );

  const { completed, pending } = useMemo(
    () =>
      streaming
        ? splitCompletedMarkdown(normalized)
        : { completed: '', pending: '' },
    [streaming, normalized]
  );

  // Two memos, so the cost of a completed line is the cost of the block being
  // written and not of the whole answer so far: `settled` changes only when a
  // block ends, `current` only when a line does.
  const { settled, current } = useMemo(
    () => splitSettledBlocks(completed),
    [completed]
  );

  const settledBlocks = useMemo(
    () => settled.split(/\n{2,}/).filter((block) => block.trim()),
    [settled]
  );

  /**
   * Every block the reader has already laid eyes on.
   *
   * A block only enters with the reveal if settling is the FIRST time it is
   * drawn: a block that was on screen as the one being written is already
   * there, and blur-fading it in again reads as a flicker. Seeded with
   * whatever was settled on the first render — a history load, a re-mount, a
   * reload — and added to as the block being written is drawn, so what is
   * left to reveal is blocks that arrived whole: multi-block deltas, and
   * history hydrated under a running turn.
   */
  const seenBlocks = useRef<Set<string> | null>(null);
  if (seenBlocks.current === null) {
    seenBlocks.current = new Set(settledBlocks.map(blockRevealKey));
  }

  // Each settled block is parsed ONCE and kept: without the cache, every new
  // block re-parsed all the blocks before it, which is the same quadratic cost
  // one level up. The revealed clone is cached beside it for the same reason,
  // and so React sees the identical elements on every later render.
  const parsedBlocks = useRef(new Map<string, ReactNode>());
  const revealedBlocks = useRef(new Map<string, ReactNode>());

  const settledContent = useMemo(() => {
    if (settledBlocks.length === 0) return null;
    return settledBlocks.map((block, blockIndex) => {
      // Keyed by the mode too: the same block parses differently with the
      // watch token on.
      const cacheKey = watchTokens ? `watch:${block}` : block;
      let parsed = parsedBlocks.current.get(cacheKey);
      if (!parsed) {
        parsed = renderMarkdownBlocks(block, undefined, undefined, {
          watchTokens,
        });

        parsedBlocks.current.set(cacheKey, parsed);
      }

      if (!seenBlocks.current?.has(blockRevealKey(block))) {
        let revealed = revealedBlocks.current.get(cacheKey);
        if (!revealed) {
          revealed = withRevealClass(parsed);
          revealedBlocks.current.set(cacheKey, revealed);
        }
        parsed = revealed;
      }

      // Blocks only ever append while an answer streams, so the position is
      // a stable identity; the fragment scopes each parse's own keys, which
      // all start from zero.
      return <Fragment key={blockIndex}>{parsed}</Fragment>;
    });
  }, [settledBlocks, watchTokens]);

  const currentContent = useMemo(() => {
    if (!current) return null;
    // Drawing the block being written is what makes it "seen", so settling is
    // not its first appearance and it must not enter again.
    seenBlocks.current?.add(blockRevealKey(current));
    return renderMarkdownBlocks(current, undefined, undefined, { watchTokens });
  }, [current, watchTokens]);

  const content = useMemo(() => {
    if (isStreaming) return null;
    return tooLong
      ? [decodeHtmlEntities(normalized)]
      : renderMarkdownBlocks(normalized, undefined, undefined, { watchTokens });
  }, [isStreaming, tooLong, normalized, watchTokens]);

  if (content !== null) {
    return <div className={containerClass}>{content}</div>;
  }

  if (!streaming) {
    return (
      <div
        className={cn(
          'chat-motion whitespace-pre-wrap break-words text-fg-primary',
          fadesInWholeAnswer.current && 'animate-chat-fade-in'
        )}
      >
        {normalized}
      </div>
    );
  }

  /**
   * The caret rides the writing edge: the partial line if there is one, else
   * the end of the block being written. A block that cannot hold it inline —
   * a table, a list — takes it underneath rather than inside its markup.
   */
  const caret = <StreamCaret />;

  return (
    <div className={containerClass}>
      {/* Keyed fragments: each parse numbers its blocks from zero, so without
          them the settled blocks and the one being written would collide on
          `p-0`. */}
      <Fragment key="settled">{settledContent}</Fragment>
      <Fragment key="current">
        {pending || !currentContent
          ? currentContent
          : appendStreamCaret(currentContent, caret)}
      </Fragment>
      {pending ? (
        <div className="whitespace-pre-wrap break-words">
          {decodeHtmlEntities(pending)}
          {caret}
        </div>
      ) : null}
      {!pending && !currentContent ? caret : null}
    </div>
  );
};

export default ChatMarkdown;
